import { useLocale } from '@solinkcorp/locale';
import { Checkbox } from '@solinkcorp/solink-ui/components/Checkbox';
import { Copy } from 'lucide-react';
import { useController, useFormContext, type Control } from 'react-hook-form';
import {
  DEFAULT_TIME_END,
  DEFAULT_TIME_START,
  type DayPartFormData,
} from '../../../hooks/useDayPartForm';
import { DayPartTimeInput } from './DayPartTimeInput';

import { type DayIndex } from '../../../day-part/utils';

interface DayPartDayRowProps {
  dayIndex: DayIndex;
  dayLabel: string;
  control: Control<DayPartFormData>;
  showCopyToAll?: boolean;
  onCopyToAll?: () => void;
}

export const DayPartDayRow = ({
  dayIndex,
  dayLabel,
  control,
  showCopyToAll,
  onCopyToAll,
}: DayPartDayRowProps) => {
  const locale = useLocale();
  const formLocale = locale.serviceDisplayMonitor.dayPartDialog;
  const { setValue, clearErrors } = useFormContext<DayPartFormData>();
  const activeField = useController({ control, name: `days.${dayIndex}.active` });
  const startField = useController({ control, name: `days.${dayIndex}.start` });
  const endField = useController({ control, name: `days.${dayIndex}.end` });

  const isActive = activeField.field.value;
  const startValue = startField.field.value;
  const endValue = endField.field.value;
  const timeRangeError =
    isActive && startValue && endValue && endValue <= startValue ? formLocale.timeRangeError : null;

  const startError = startField.fieldState.error;
  const endError = endField.fieldState.error;
  const displayError = timeRangeError || startError?.message || endError?.message;

  return (
    <div className="flex flex-col gap-0.5 py-1">
      <div className="flex items-center gap-3">
        <Checkbox
          className="border-border-strong text-interaction-primary-content-default"
          checked={isActive}
          onCheckedChange={(checked) => {
            activeField.field.onChange(checked === true);

            setValue(`days.${dayIndex}.start`, DEFAULT_TIME_START);
            setValue(`days.${dayIndex}.end`, DEFAULT_TIME_END);
            clearErrors(`days.${dayIndex}`);
          }}
        />

        <span className="w-[100px] text-foreground-primary label-md">{dayLabel}</span>

        <div>
          <div className="flex items-center gap-2">
            <DayPartTimeInput
              value={startField.field.value ?? ''}
              onChange={startField.field.onChange}
              disabled={!isActive}
              hasError={!!timeRangeError || !!startError}
            />

            <span className="text-text-primary body-md-strong">{formLocale.to}</span>

            <div className="inline-flex w-fit justify-start">
              <DayPartTimeInput
                value={endField.field.value ?? ''}
                onChange={endField.field.onChange}
                disabled={!isActive}
                hasError={!!timeRangeError || !!endError}
              />
              {showCopyToAll && (
                <button
                  type="button"
                  onClick={onCopyToAll}
                  className="ml-3 flex items-center gap-2 rounded-lg px-3 py-2 text-foreground-primary transition-colors hover:bg-accent"
                >
                  <Copy className="size-4" />
                  <span className="label-md">{formLocale.copyToAll}</span>
                </button>
              )}
            </div>
          </div>
          {displayError && <p className="pl-2 pt-1 text-text-error caption">{displayError}</p>}
        </div>
      </div>
    </div>
  );
};
